import { AxiosError } from 'axios';
import VehiclesGrid from '../../components/grids/VehiclesGrid'
import AddVehicleForm from '../../components/FormComps/Vehicles/AddVehicleForm'
import EditVehicleForm from '../../components/FormComps/Vehicles/EditVehicleForm'

import { useState, useEffect, useMemo } from 'react'
import { useToast } from '../../contexts/ToastContext'

import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import { GridDataStateChangeEvent } from '@progress/kendo-react-grid'
import { process } from '@progress/kendo-data-query'
import { FaTimesCircle } from 'react-icons/fa'
import {
  getVehicles,
  submitVehicle,
  updateVehicle,
  deleteVehicle,
} from '../../data/vehicleData'
import {
  Vehicle,
  PostVehicle,
  UpdateVehicle,
} from '../../data/interfaces'

import { canEditArea } from '../../helpers/areaAccess'

import '../Styles.css'

interface VehiclesProps {
  allowedRoutes?: string[]
}

const Vehicles = (props: VehiclesProps) => {
  // Vehicles.Manage or an administrator (see areaAccess.ts). The server enforces the same (Manage.Vehicles.*).
  const canEdit = canEditArea(props.allowedRoutes, 'Vehicles')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [vehicleData, setVehicleData] = useState<Vehicle[]>([])
  const [totalRecords, setTotalRecords] = useState(0)
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [editFormVisible, setEditFormVisible] = useState(false)
  const [addFormVisible, setAddFormVisible] = useState(false)
  const [vehicleId, setVehicleId] = useState(0)
  // Vehicles that have left the fleet are hidden unless the grid toggle is on
  const [showReturnedSold, setShowReturnedSold] = useState(false)
  const { showToast } = useToast()

  // Data state for Kendo Grid (for future server-side implementation)
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 25,
    filter: undefined,
    sort: undefined
  })

  const handleAddForm = () => {
    setAddFormVisible((prevState) => (!prevState ? true : true))
    setEditFormVisible(false)
    handleExpanded()
  }

  const handleEditForm = (id: number) => {
    console.log("handleEditForm called with ID:", id);
    setVehicleId(id)
    setEditFormVisible((prevState) => (!prevState ? true : true))
    setAddFormVisible(false)
    handleExpanded()
  }

  const updateVehicleHandler = (dataItem: UpdateVehicle, id: number) => {
    console.log('Updating vehicle with data:', dataItem);
    updateVehicle(dataItem, id).then(() => {
      const fetchData = async () => {
        const vehicleData = await getVehicles()
        setVehicleData(vehicleData)
        setTotalRecords(vehicleData.length)
        handleExpanded()
        showToast('Vehicle successfully updated', 'success')
      }
      fetchData()
    }).catch((error) => {
        console.error('Error updating vehicle:', error);
        handleExpanded()
        showToast(`Error updating vehicle: ${error.message}`, 'error')
        setErrorMessage(error.message)
      })
  }

  const newVehicleHandler = async (dataItem: PostVehicle) => {
    try {
      await submitVehicle(dataItem);

      const fetchData = async () => {
        const vehicleData = await getVehicles();
        setVehicleData(vehicleData);
        setTotalRecords(vehicleData.length);
      };
      fetchData();

      handleExpanded();
      showToast('Vehicle successfully added', 'success');
      setErrorMessage(null);
    } catch (err) {
      handleExpanded();
      const axiosErr = err as AxiosError;
      
      if (axiosErr.response?.status === 400) {
        showToast("Invalid vehicle data. Please check all fields.", "error");
      } else if (axiosErr.response?.status === 409) {
        showToast("A vehicle with this registration already exists.", "error");
      } else {
        showToast("Failed to add vehicle. Please try again.", "error");
      }

      setErrorMessage(axiosErr.message);
    }
  };

  const loadVehicles = async () => {
    try {
      setLoading(true);
      const vehicles = await getVehicles();
      setVehicleData(vehicles);
      setTotalRecords(vehicles.length);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      showToast('Error loading vehicles', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadVehicles();
  }, [])

  const deleteVehicleHandler = (id: number) => {
    deleteVehicle(id)
      .then(() => {
        setVehicleData((prevData) =>
          prevData.filter((vehicle) => vehicle.vehicleId !== id)
        )
        setTotalRecords((prev) => prev - 1)
        handleExpanded()
        showToast('Vehicle successfully deleted', 'success')  
      })
      .catch((error) => {
        // Handle error, for example, showing a toast message about the failure
        console.log('Error deleting vehicle: ' + error.message)
        handleExpanded()
        showToast('Error deleting vehicle', 'error')
      })
    
  }

  const handleDataStateChange = (event: any) => {
    const newDataState = event.dataState;
    setDataState(newDataState);

  };

  const handleExpanded = () => {
    setExpanded((prevState) => !prevState)

    if (editFormVisible) {
      setEditFormVisible(false)
    }
    if (addFormVisible) {
      setAddFormVisible(false)
    }
  }

  const handleSelect = () => {
    setExpanded(false)
  }

  const handleExport = () => {
    // The grid handles its own export
  }

  const handleShowReturnedSoldChange = (value: boolean) => {
    setShowReturnedSold(value)
    // The row count changes, so go back to the first page
    setDataState((prevState) => ({ ...prevState, skip: 0 }))
  }

  // Process data with client-side filtering, sorting, and paging
  const processedData = useMemo(() => {
    const visibleVehicles = showReturnedSold
      ? vehicleData
      : vehicleData.filter((vehicle) => !vehicle.dateReturnedSold)

    return process(visibleVehicles, dataState);
  }, [vehicleData, dataState, showReturnedSold]);

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={750}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={handleExpanded}
        onSelect={handleSelect}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addFormVisible && canEdit && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add Vehicle</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <AddVehicleForm
                    onVehicleSubmit={newVehicleHandler}
                    onErrorMessage={errorMessage}
                  />
                </div>
              </>
            )}

            {editFormVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>{canEdit ? 'Edit Vehicle' : 'Vehicle'}</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <EditVehicleForm
                    key={vehicleId} // Force re-render when vehicleId changes
                    onEditSubmit={updateVehicleHandler}
                    onVehicleId={vehicleId}
                    onVehicleDelete={deleteVehicleHandler}
                    canEdit={canEdit}
                  />
                </div>
              </>
            )}
          </DrawerNavigation>
        </DrawerContent>
      </Drawer>

      <VehiclesGrid
        data={processedData}
        loading={loading}
        dataState={dataState}
        onDataStateChange={handleDataStateChange}
        onAddToggle={handleAddForm}
        onEditToggle={handleEditForm}
        onExport={handleExport}
        showReturnedSold={showReturnedSold}
        onShowReturnedSoldChange={handleShowReturnedSoldChange}
        canEdit={canEdit}
      />
    </div>
  )
}

export default Vehicles